import BaseView from '../../../js/base-view';

import Field from './fields/field';
import FieldState from './fields/field-state';

const SELECTOR_COUNTRY_FIELD = '[data-field-country]';
const SELECTOR_STATE_FIELD = '[data-field-state]';
const SELECTOR_UPDATE_CHECKOUT_FIELD = '[data-update-checkout]';

/**
 * Custom implementation of WooCommerce address-i18n & country-select scripts
 *
 * @see https://github.com/woocommerce/woocommerce/blob/4.0.1/assets/js/frontend/address-i18n.js
 * @see https://github.com/woocommerce/woocommerce/blob/4.0.1/assets/js/frontend/country-select.js
 */
export default class FormAddress extends BaseView {
	init() {
		if (typeof window.wc_country_select_params === 'undefined' ||
			typeof window.wc_address_i18n_params === 'undefined') {
			return false;
		}

		this.config = {
			states: JSON.parse(window.wc_country_select_params.countries.replace(/&quot;/g, '"')),
			locale: JSON.parse(window.wc_address_i18n_params.locale.replace(/&quot;/g, '"')),
			locale_fields: JSON.parse(window.wc_address_i18n_params.locale_fields.replace(/&quot;/g, '"')),
		};

		this.refs = {
			address_1: new Field(this.el.querySelector(this.config.locale_fields.address_1)),
			address_2: new Field(this.el.querySelector(this.config.locale_fields.address_2)),
			city: new Field(this.el.querySelector(this.config.locale_fields.city)),
			postcode: new Field(this.el.querySelector(this.config.locale_fields.postcode)),
			state: new FieldState(this.el.querySelector(SELECTOR_STATE_FIELD)),
		};

		Object.keys(this.config.locale.default).forEach((key) => {
			if (!this.refs[key]) {
				this.refs[key] = new Field(this.el.querySelector(`[id*="${key}"]`));
			}
		});

		this.bindEvents();
		this.reorderFields(this.refs.country.getValue());

		return this;
	}

	bindEvents() {
		this.on('change', `${ SELECTOR_COUNTRY_FIELD } select`, (e) => this.onCountryChange(e.target.value));
		this.on('change', `${ SELECTOR_UPDATE_CHECKOUT_FIELD }`, this.updateCheckout.bind(this));

	}

	onCountryChange(country) {
		this.updateFieldLocale('address_1', this.config.locale[ country ]);
		this.updateFieldLocale('address_2', this.config.locale[ country ]);
		this.updateFieldLocale('city', this.config.locale[ country ]);
		this.updateFieldLocale('postcode', this.config.locale[ country ]);
		this.updateStateLocale(country);
		this.reorderFields(country);
	}

	updateFieldLocale(fieldId, locale) {
		const field = this.refs[ fieldId ];

		const fieldLocale = ((locale && locale[ fieldId ]) || this.config.locale.default[ fieldId ]);

		field.setRequired(fieldLocale.required);
		field.changeLabel(fieldLocale.label);
		field[ fieldLocale.hidden ? 'hide' : 'show' ]();
	}

	updateStateLocale(country) {
		const locale = { ...this.config.locale.default.state, ...(this.config.locale[ country ] ? this.config.locale[ country ].state : {}) };

		this.refs.state.updateOptions(this.config.states[ country ]);
		this.refs.state.setRequired(locale.required);
		this.refs.state.changeLabel(locale.label);
	}

	reorderFields(country) {
		const fields = { ...this.config.locale.default, ...(this.config.locale[ country ] ? this.config.locale[ country ] : {}) };

		for (const [key, value] of Object.entries(fields)) {
			this.refs[key].setPriority(value.priority);
		}
	}

	async updateCheckout(event) {
		if (event.target.dataset.updateCheckout === 'billing'
			&& window.supt && window.supt.restAPI && window.supt.restAPI.user) {
			await window.fetch(`${window.supt.restAPI.root}/supt/v1/users/${window.supt.restAPI.user}`, {
				credentials: 'same-origin',
				method: 'POST',
				headers: {
					'X-WP-Nonce': window.supt.restAPI.nonce,
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ billing_country: event.target.value })
			});

			// Event for Currency selector in navigation
			document.body.dispatchEvent(new CustomEvent('update-currency'));
		}

		// WooCommerce custom event
		// https://github.com/woocommerce/woocommerce/blob/4.0.1/assets/js/frontend/checkout.js#L19
		document.body.dispatchEvent(new CustomEvent('update_checkout'));
	}
}
